import type * as React from 'react'
import type { MouseEvent } from 'react'
import { flexRender, type RowData, type Table as TanstackTable } from '@tanstack/react-table'
import { DataTablePagination } from 'shared/components/data-table/data-table-pagination'
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from 'shared/components/ui/table'
import { useTranslate } from 'shared/lib/i18n'
import { cn } from 'shared/lib/utils'

import { Loader } from '../ui/Loader'

type DataTableProps<TData> = {
	table: TanstackTable<TData>
	withPagination?: boolean
	actionBar?: React.ReactNode
	isFetching?: boolean
	isRefetching?: boolean
	/** What the table's one empty row says, under its header, when it has no rows; «Нет результатов.» by default. */
	emptyText?: React.ReactNode
	/** The scroll box's height; the viewport less the page's chrome by default. */
	maxHeightClassName?: string
	row?: {
		/** Extra attributes of a row — `data-testid`, `data-state`, … */
		attributes?: (row: TData) => Record<string, string | number | boolean | undefined>
		onRowClick?: (row: TData, event: MouseEvent) => void
		onRowContextMenu?: (row: TData, event: MouseEvent) => void
		onDoubleRowClick?: (row: TData, event: MouseEvent) => void
		className?: string
	}
}

export function DataTable<TData>({
	table,
	actionBar,
	isFetching,
	isRefetching,
	row,
	emptyText,
	maxHeightClassName = 'max-h-[calc(100vh-250px)]',
	withPagination = true,
}: DataTableProps<TData>) {
	const { t } = useTranslate()
	return (
		<div className={cn('flex w-full flex-col gap-2.5')}>
			<div className='rounded-md border overflow-hidden relative'>
				<div
					className={cn(
						'absolute flex z-3 inset-0 items-center justify-center overflow-hidden bg-white/0 transition-colors duration-100 pointer-events-none',
						isFetching && isRefetching && 'bg-white/80',
						isFetching && !isRefetching && 'bg-white/30',
					)}
				>
					{isFetching && isRefetching && (
						<div className='absolute z-3'>
							<Loader />
						</div>
					)}
				</div>

				<Table containerClassName={maxHeightClassName}>
					<TableHeader className='ring-1 ring-border ring-offset-0 bg-background sticky top-0 z-3'>
						{table.getHeaderGroups().map((headerGroup) => (
							<TableRow key={headerGroup.id}>
								{headerGroup.headers.map((header) => (
									<TableHead
										key={header.id}
										colSpan={header.colSpan}
										className={cn('font-semibold', header.column.columnDef.meta?.headClassName)}
									>
										{header.isPlaceholder
											? null
											: flexRender(
													header.column.columnDef.header,
													header.getContext(),
												)}
									</TableHead>
								))}
							</TableRow>
						))}
					</TableHeader>
					<TableBody>
						{table.getRowModel().rows?.length ? (
							table.getRowModel().rows.map((rowData) => (
								<TableRow
									key={rowData.id}
									data-state={rowData.getIsSelected() && 'selected'}
									{...row?.attributes?.(rowData.original)}
									onClick={
										row?.onRowClick
											? (event) => row.onRowClick!(rowData.original, event)
											: undefined
									}
									onDoubleClick={
										row?.onDoubleRowClick
											? (event) =>
													row.onDoubleRowClick!(rowData.original, event)
											: undefined
									}
									onContextMenu={
										row?.onRowContextMenu
											? (event) => {
													event.preventDefault()
													row.onRowContextMenu!(rowData.original, event)
												}
											: undefined
									}
									className={cn(row?.onRowClick && 'cursor-pointer', row?.className)}
								>
									{rowData.getVisibleCells().map((cell) => (
										<TableCell
											key={cell.id}
											className={cell.column.columnDef.meta?.cellClassName}
										>
											{flexRender(
												cell.column.columnDef.cell,
												cell.getContext(),
											)}
										</TableCell>
									))}
								</TableRow>
							))
						) : (
							<TableRow className='hover:bg-transparent'>
								<TableCell
									colSpan={table.getVisibleLeafColumns().length}
									className='h-16 text-center text-muted-foreground'
								>
									{emptyText ?? t('dataTable.empty')}
								</TableCell>
							</TableRow>
						)}
					</TableBody>
				</Table>
			</div>
			<div className='flex flex-col gap-2.5'>
				{withPagination && <DataTablePagination table={table} />}
				{actionBar &&
					table.getFilteredSelectedRowModel().rows.length > 0 &&
					actionBar}
			</div>
		</div>
	)
}

declare module '@tanstack/react-table' {
	/** A column's own classes: right-aligned numbers, a narrow actions column. */
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	interface ColumnMeta<TData extends RowData, TValue> {
		headClassName?: string
		cellClassName?: string
	}
}
